import React, { Component } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  adoptAllPrompts,
  getAgents,
  getPrompts,
  parseApiError,
  SESSION_EXPIRED
} from '../../services/moderationDashboardService';
import { MOTIF_LABELS, isOutOfTaxonomy } from '../../lib/moderationMotifs';
import PromptDiff from './PromptDiff';
import PromptEditor, { joinRegistryWithLive } from './PromptEditor';

// Onglet « Prompts & agents » du dashboard Modération IA (B2_03 volet C en lecture ; B5_01,
// Phase 2 : édition par consigne, portée par <PromptEditor/>).
//
// Deux sources, lancées en parallèle par fetchAgents() :
//   CD3  getAgents()  — ce qui TOURNE chez Mistral : { agents: [ …6, L'ORDRE FAIT FOI… ],
//        context_agent, fetched_at } — 503 si rien n'est lisible. Ne porte JAMAIS la consigne
//        d'essai (DESCRIPTORS figé, mistralAgents.js:295).
//   CD6  getPrompts() — le REGISTRE + la réconciliation : { prompts: [ …6 (+1 hors prod)… ],
//        fetched_at, live_error } — 200 même en dégradation totale (live: null, unknown ×N).
// Jointure CD3.id === CD6.agent_id dans joinRegistryWithLive() (PromptEditor.jsx) : les cartes de
// verdict sont rendues DEPUIS LE REGISTRE (l'écran reste rempli quand CD3 est en 503, L3), l'agent
// de contexte depuis CD3 seul.
//
// ⛔ Ce fichier ne porte AUCUN champ de formulaire : l'édition vit dans PromptEditor.jsx, monté
//    dans la carte de chaque agent de verdict — jamais dans celle de l'agent de contexte (DA21).
//    Le texte en service reste rendu dans un <Box component="pre"> (lecture), pas dans un
//    TextField readOnly — un élément qui n'est pas un contrôle de formulaire ne peut pas
//    devenir éditable par accident.

// ─────────────────────────────────────────────────────────────────────────────
// ⛔ LE NOM CONSOLE FAIT FOI, JAMAIS LE SLUG DE L'ID.
//
// Les slugs contenus dans les IDs sont HISTORIQUES et MENTENT : `external-human-moderate`
// est l'agent DOUTE GC, `untitled-agent` l'agent STRICTE GC. L'ID n'est affiché qu'en
// donnée technique secondaire, pour recouper avec la console Mistral.
//
// ⚠ MAIS `console_name` vaut `null` dès que `status !== 'ok'` (mistralAgents.js:147-165).
//   Appliquer la règle à la lettre rendrait alors une carte au TITRE VIDE — exactement la
//   panne masquée que CD3+ existe pour rendre visible. D'où fallbackTitle() ci-dessous :
//   le titre se dérive de product/object/regime, qui sont connus SANS appel réseau.
// ─────────────────────────────────────────────────────────────────────────────

// ⚠ CONTRAT (B5_02 → B5_01, D22) : GROUPS, REGIME_LABELS, STATUS_LABELS, groupOf, fallbackTitle
//   et titleOf sont EXPORTÉS INLINE pour que PromptDiff.jsx (et l'écran d'édition qui suit)
//   nomment les agents sans recopier ces tables — une seconde dérivation divergerait au premier
//   changement. Contrôle mécanique : `grep -c "^export " Agents.jsx` → 7 (le défaut + 6).
//   Import circulaire assumé avec ./PromptDiff : rien n'est appelé au chargement du module.

// Clé de groupement = le COUPLE (product, object). ⛔ Grouper sur `product` seul donnerait
// 2 groupes (GC ×2, GD ×4), pas 3 : l'objet modéré est porté par une clé séparée.
export const GROUPS = {
  'GC|comment': { label: 'GC', surface: 'commentaires du widget' },
  'GD|topic': { label: 'GD sujets', surface: 'sujets du forum' },
  'GD|message': { label: 'GD messages', surface: 'messages du forum' }
};

// Valeurs réelles de la donnée : `strict` / `doubt` — JAMAIS `AI_ONLY` ni `REVIEW`, qui
// n'appartiennent qu'à la grille d'affichage (et au nom console).
export const REGIME_LABELS = {
  strict: 'IA stricte (AI_ONLY)',
  doubt: 'IA + doute (REVIEW)'
};

// Ordre d'affichage à l'intérieur d'un groupe. L'endpoint renvoie déjà strict puis doubt
// (DESCRIPTORS, « l'ordre fait foi ») ; ce tri ne fait que rendre l'écran indépendant de ça.
const REGIME_ORDER = { strict: 0, doubt: 1 };

// `missing` et `unreadable` ne disent PAS la même chose et ne doivent pas se confondre à
// l'écran (le back les distingue exprès, mistralAgents.js:82-90) :
//   missing    → l'agent n'existe plus. La surface qu'il couvre n'est plus analysée.
//                C'est une alerte d'exploitation → bandeau (décision Mathias 2026-08-27).
//   unreadable → nous n'avons pas pu le lire (timeout, 5xx, réseau). La modération elle-même
//                tourne toujours → signalé sur la fiche, JAMAIS de bandeau : une alarme qui
//                se déclenche sur un incident réseau d'une minute finit par être ignorée.
const STATUS_OK = 'ok';
const STATUS_MISSING = 'missing';
const STATUS_UNREADABLE = 'unreadable';

export const STATUS_LABELS = {
  missing: 'Introuvable chez Mistral',
  unreadable: 'Configuration illisible'
};

// `status_detail` est fabriqué par toTypedFailure (mistralAgents.js:230-256) : aucun contenu
// amont brut, aucun secret. Les codes non listés (réseau : ECONNABORTED, ENOTFOUND…) et la
// famille `upstream_http_<n>` sont rendus par detailLabel() ci-dessous.
const DETAIL_LABELS = {
  agent_not_found: 'l\'agent n\'existe plus dans le compte Mistral (404)',
  invalid_api_key: 'clé d\'API refusée par Mistral (401)',
  forbidden: 'accès refusé par Mistral (403)',
  malformed_agent_payload: 'réponse reçue inexploitable (ce n\'est pas un agent)'
};

function groupKey(agent) {
  return `${agent.product}|${agent.object}`;
}

export function groupOf(agent) {
  return GROUPS[groupKey(agent)] || null;
}

// `regime` est une clé structurée (`strict` / `doubt`). Une valeur INCONNUE (nouveau régime
// ajouté côté back) doit rester visible telle quelle — la faire disparaître la rendrait
// indistinguable du `regime: null` PAR CONCEPTION du context_agent.
function regimeLabel(regime) {
  if (regime === null || regime === undefined) return null;
  return REGIME_LABELS[regime] || `régime « ${regime} »`;
}

// Titre de repli quand `console_name` est null (entrée non lue). Jamais un titre vide, jamais
// le slug promu en libellé : product/object/regime sont connus des constantes AGENT_*, donc
// disponibles même pour un agent que Mistral ne nous a pas rendu.
// ⚠ `isContext` n'est pas cosmétique : sans lui, un agent de contexte non lu s'intitulerait
//   « Agent de modération » — exactement la lecture « 7 agents de modération » que D8 interdit.
export function fallbackTitle(agent, isContext) {
  if (isContext) return 'Agent de contexte';
  const group = groupOf(agent);
  const regime = regimeLabel(agent.regime);
  if (group && regime) return `${group.label} — ${regime}`;
  if (group) return group.label;
  return 'Agent de modération';
}

export function titleOf(agent, isContext) {
  return agent.console_name || fallbackTitle(agent, isContext);
}

function detailLabel(detail) {
  if (!detail) return null;
  if (DETAIL_LABELS[detail]) return DETAIL_LABELS[detail];
  const upstream = /^upstream_http_(\d+)$/.exec(detail);
  if (upstream) return `erreur Mistral (HTTP ${upstream[1]})`;
  return `lecture impossible (${detail})`;
}

// Le corps d'un 503 n'arrive JAMAIS parsé : http.js:9-12 fait
// `throw new Error(\`HTTP ${status}: ${text}\`)` — le texte du corps est concaténé dans le
// message. Sans cette extraction, l'écran afficherait `HTTP 503: {"error":…}` au lieu des
// messages FR que le contrôleur a rédigés pour l'admin.
function parseErrorBody(err) {
  const message = (err && err.message) || '';
  const brace = message.indexOf('{');
  if (brace === -1) return null;
  try {
    const body = JSON.parse(message.slice(brace));
    return body && typeof body.error === 'string' ? body : null;
  } catch (parseError) {
    // Corps non-JSON (page d'erreur d'un proxy, corps tronqué) : on retombe sur le message brut.
    return null;
  }
}

// `response_format_raw` vaut `null` sur les 7 agents AUJOURD'HUI — mais chez Mistral ce champ
// est un OBJET, et la Phase 2 est précisément le ticket qui éditera ces agents. `String()`
// afficherait alors « [object Object] ».
function formatRawValue(value) {
  if (value === null || value === undefined) return 'aucun';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value);
  } catch (serializeError) {
    return 'valeur non affichable';
  }
}

function formatFetchedAt(iso) {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString('fr-FR');
}

class Agents extends Component {
  constructor(props) {
    super(props);
    this.state = {
      data: null,
      isLoading: false,
      error: null,
      diffOpen: false,
      // B5_01 — registre CD6 (joint à CD3 au rendu), rapport du dernier amorçage global,
      // éditeurs portant un texte modifié non enregistré (L4).
      registry: null,
      registryError: null,
      adoptAllBusy: false,
      adoptAllReport: null,
      dirtyAgents: {}
    };
    this.fetchAgents = this.fetchAgents.bind(this);
    this.onRefresh = this.onRefresh.bind(this);
    this.openDiff = this.openDiff.bind(this);
    this.closeDiff = this.closeDiff.bind(this);
    this.adoptAll = this.adoptAll.bind(this);
    this.dismissReport = this.dismissReport.bind(this);
    this.handleDirtyChange = this.handleDirtyChange.bind(this);
  }

  // B5_02 — ouverture / fermeture du dialogue de comparaison. Le dialogue vit dans PromptDiff ;
  // ici on ne porte que l'état d'ouverture.
  openDiff() {
    this.setState({ diffOpen: true });
  }

  closeDiff() {
    this.setState({ diffOpen: false });
  }

  componentDidMount() {
    this._isMounted = true;
    this.fetchAgents();
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  // ⚠ ModerationDashboard.jsx rend `{tab === 'agents' && <Agents />}` : le composant est
  //   DÉMONTÉ dès qu'on quitte l'onglet, donc ce fetch repart à chaque visite — et il
  //   déclenche 7 appels live vers l'API bêta de Mistral. Assumé pour ce ticket : le cache
  //   vivrait dans le conteneur, qui n'est pas dans le write-set de B2_03. D'où le bouton
  //   « Rafraîchir » plutôt qu'un rechargement implicite.
  fetchAgents() {
    // `diffOpen` remis à faux : un rafraîchissement en échec démonte le dialogue (le bloc de
    // données n'est plus rendu) et, sans ça, le succès suivant le ferait ROUVRIR tout seul.
    // ⚠ B5_01 greffe CD6 ici en PRÉSERVANT ce champ (contrat B5_02).
    this.setState({ isLoading: true, error: null, registryError: null, diffOpen: false });
    // Les deux appels sont indépendants : CD3 peut être en 503 (clé absente, Mistral
    // injoignable) pendant que CD6 répond 200 — et réciproquement. Aucun ne masque l'autre.
    // Comme avant B5_01, un échec ne vide pas la donnée précédente (`data` / `registry`).
    Promise.allSettled([getAgents(), getPrompts()]).then(([agentsResult, registryResult]) => {
      if (!this._isMounted) return;
      const next = { isLoading: false };
      if (agentsResult.status === 'fulfilled') {
        next.data = agentsResult.value;
        next.error = null;
      } else {
        next.error = agentsResult.reason || new Error('Erreur de chargement');
      }
      if (registryResult.status === 'fulfilled') {
        next.registry = registryResult.value;
        next.registryError = null;
      } else {
        next.registryError = registryResult.reason || new Error('Erreur de chargement');
      }
      this.setState(next);
    });
  }

  onRefresh(e) {
    if (e && e.preventDefault) e.preventDefault();
    this.fetchAgents();
  }

  // B5_01 — perte de saisie (L4) : l'état « texte modifié, non enregistré » remonte de chaque
  // éditeur jusqu'au conteneur, qui garde l'onglet tant que l'admin n'a pas confirmé.
  handleDirtyChange(agentId, dirty) {
    if (!this._isMounted) return;
    this.setState(
      (prev) => {
        const dirtyAgents = { ...prev.dirtyAgents };
        if (dirty) dirtyAgents[agentId] = true;
        else delete dirtyAgents[agentId];
        return { dirtyAgents };
      },
      () => {
        if (this.props.onDirtyChange) {
          this.props.onDirtyChange(Object.keys(this.state.dirtyAgents).length > 0);
        }
      }
    );
  }

  // D20 / CD7 — amorçage global : UN instantané Mistral, un rapport PAR consigne. ⛔ Toujours
  // 200, même quand une adoption échoue (`skipped[]`) : le rapport est rendu nominativement,
  // jamais résumé en « amorçage réussi ».
  adoptAll() {
    this.setState({ adoptAllBusy: true, adoptAllReport: null });
    adoptAllPrompts()
      .then((report) => {
        if (!this._isMounted) return;
        this.setState({ adoptAllBusy: false, adoptAllReport: { report } });
        this.fetchAgents();
      })
      .catch((err) => {
        if (!this._isMounted) return;
        this.setState({ adoptAllBusy: false, adoptAllReport: { error: parseApiError(err) } });
      });
  }

  dismissReport() {
    this.setState({ adoptAllReport: null });
  }

  // Bandeau d'alerte — décision Mathias 2026-08-27 : réservé aux DISPARITIONS (`missing`).
  // Un agent disparu = une surface entière qui n'est plus analysée ; c'est ce fait-là qui
  // mérite de crier, pas une coupure de lecture passagère.
  // ⚠ Portée volontairement limitée aux agents de VERDICT : `context_agent` ne rend aucun
  //   verdict, sa disparition dégrade le contexte fourni au modèle, elle ne laisse aucune
  //   surface non modérée. Son état est signalé sur sa propre fiche.
  renderMissingBanner(agents) {
    // ⛔ La consigne d'essai (B5_01, D29) ne couvre aucune surface : jamais dans ce bandeau —
    //    filtre EXPLICITE, en plus de l'exclusion structurelle (la liste vient de CD3, qui ne la
    //    porte pas ; si un jour la liste jointe CD3 × CD6 l'alimente, le filtre tient).
    const missing = agents.filter((agent) => agent.status === STATUS_MISSING && !agent.is_test_prompt);
    if (!missing.length) return null;

    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        <AlertTitle>
          {missing.length === 1
            ? 'Une analyse automatique a disparu'
            : `${missing.length} analyses automatiques ont disparu`}
        </AlertTitle>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {missing.length === 1
            ? 'L\'agent ci-dessous est introuvable chez Mistral : la surface qu\'il couvre n\'est plus analysée.'
            : 'Les agents ci-dessous sont introuvables chez Mistral : les surfaces qu\'ils couvrent ne sont plus analysées.'}
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 3 }}>
          {missing.map((agent) => {
            const group = groupOf(agent);
            return (
              <li key={agent.id}>
                <Typography variant="body2">
                  <strong>{fallbackTitle(agent)}</strong>
                  {group ? ` — ${group.surface}` : null}
                </Typography>
              </li>
            );
          })}
        </Box>
      </Alert>
    );
  }

  renderError() {
    const { error } = this.state;
    const body = parseErrorBody(error);

    if (!body) {
      return (
        <Alert severity="error">
          Impossible de charger la configuration des agents : {(error && error.message) || 'erreur inconnue'}
        </Alert>
      );
    }

    // `mistral_not_configured` n'est PAS une panne Mistral : c'est un environnement sans clé
    // provisionnée (le local ou une DEV sans secret). Le peindre en rouge enverrait chercher
    // un incident qui n'existe pas.
    const isNotConfigured = body.error === 'mistral_not_configured';
    const details = Array.isArray(body.details) ? body.details : [];

    return (
      <Alert severity={isNotConfigured ? 'warning' : 'error'}>
        {/* Titre court et DISTINCT du corps : le back rédige déjà une phrase complète,
            reprendre la même en titre la ferait lire deux fois. */}
        <AlertTitle>
          {isNotConfigured ? 'Clé Mistral absente de cet environnement' : 'Mistral injoignable'}
        </AlertTitle>
        <Typography variant="body2">{body.message}</Typography>
        {details.length > 0 && (
          <Box component="ul" sx={{ m: 0, mt: 1, pl: 3 }}>
            {details.map((detail) => (
              <li key={detail.id}>
                <Typography variant="caption" color="text.secondary">
                  {detail.id} — {detailLabel(detail.detail) || 'motif inconnu'}
                </Typography>
              </li>
            ))}
          </Box>
        )}
      </Alert>
    );
  }

  // ⚠ `isContext` change le SENS du message dégradé, pas seulement sa formulation : l'agent de
  //   contexte ne rend aucun verdict, sa disparition ne laisse AUCUNE surface non analysée —
  //   elle prive les analyses de leur résumé de page. Sans ce drapeau, la fiche affirmerait
  //   « la surface qu'il couvre n'est plus analysée », soit l'inverse de ce que le bandeau
  //   (volontairement muet sur cet agent) tient pour vrai.
  renderAgentCard(agent, isContext) {
    const isOk = agent.status === STATUS_OK;
    const statusLabel = STATUS_LABELS[agent.status] || 'État inattendu';
    const detail = detailLabel(agent.status_detail);

    return (
      <Paper
        key={agent.id}
        variant="outlined"
        sx={{ p: 2 }}
        data-testid={isContext ? 'context-agent-card' : 'prompt-card'}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          sx={{ mb: 1 }}
        >
          <Typography variant="subtitle1">{titleOf(agent, isContext)}</Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            {/* Aucune borne codée en dur sur la version : la console Mistral n'est pas gelée
                avant la Phase 2 (DA10), les valeurs observées bougent. */}
            {agent.version_active !== null && agent.version_active !== undefined && (
              <Tooltip title="Version active de l'agent chez Mistral">
                <Chip size="small" label={`v${agent.version_active}`} />
              </Tooltip>
            )}
            {!isOk && (
              <Chip
                size="small"
                color={agent.status === STATUS_MISSING ? 'error' : 'warning'}
                label={statusLabel}
              />
            )}
          </Stack>
        </Stack>

        {!isOk && (
          <Alert
            severity={agent.status === STATUS_MISSING ? 'error' : 'warning'}
            sx={{ mb: 2 }}
          >
            {this.degradedMessage(agent, isContext)}
            {detail ? ` (${detail})` : null}
          </Alert>
        )}

        {this.renderAgentFields(agent)}

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Identifiant technique : {agent.id}
        </Typography>

        {this.renderPrompt(agent)}
      </Paper>
    );
  }

  // B5_01 — carte d'un agent de VERDICT rendue depuis le REGISTRE (entrée jointe CD3 × CD6,
  // cf. joinRegistryWithLive) : reste rendue quand CD3 est en 503 (cd3 = null → champs « — »,
  // pas de texte en service), et porte l'éditeur. ⛔ Jamais appelée pour l'agent de contexte
  // (DA21) — lui reste sur renderAgentCard(contextAgent, true).
  renderRegistryCard(entry) {
    const { registry } = this.state;
    const cd3 = entry.cd3;
    const status = cd3 ? cd3.status : entry.liveStatus;
    const isOk = status === STATUS_OK;
    const statusLabel = STATUS_LABELS[status] || (status ? 'État inattendu' : 'Non lu');
    const detail = detailLabel(cd3 ? cd3.status_detail : entry.liveDetail);
    // Forme CD3 reconstituée pour renderAgentFields quand CD3 n'a pas servi cet agent
    // (regime ← agent_mode, C2 — le remap est fait au point de jointure).
    const fields = cd3 || {
      product: entry.product,
      object: entry.object,
      regime: entry.regime,
      model: null,
      temperature: null,
      response_format: null
    };
    const version =
      cd3 && cd3.version_active !== null && cd3.version_active !== undefined
        ? cd3.version_active
        : entry.liveVersion;

    return (
      <Paper key={entry.agentId} variant="outlined" sx={{ p: 2 }} data-testid="prompt-card">
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          sx={{ mb: 1 }}
        >
          <Typography variant="subtitle1">{entry.title}</Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            {entry.isTest && (
              <Chip size="small" color="warning" label="consigne d'essai — jetable" data-testid="prompt-test-badge" />
            )}
            {version !== null && version !== undefined && (
              <Tooltip title="Version active de l'agent chez Mistral">
                <Chip size="small" label={`v${version}`} />
              </Tooltip>
            )}
            {!isOk && (
              <Chip
                size="small"
                color={status === STATUS_MISSING ? 'error' : 'warning'}
                label={statusLabel}
              />
            )}
          </Stack>
        </Stack>

        {!isOk && status && (
          <Alert severity={status === STATUS_MISSING ? 'error' : 'warning'} sx={{ mb: 2 }}>
            {/* La consigne d'essai ne couvre aucune surface : son message dégradé ne doit pas
                dire « la surface qu'elle couvre n'est plus analysée » (D29). */}
            {entry.isTest
              ? status === STATUS_MISSING
                ? 'La consigne d\'essai est introuvable chez Mistral (identifiant configuré inexistant ?). Aucune surface de modération n\'est concernée.'
                : 'La consigne d\'essai n\'a pas pu être lue. Aucune surface de modération n\'est concernée.'
              : this.degradedMessage(cd3 || { status }, false)}
            {detail ? ` (${detail})` : null}
          </Alert>
        )}

        {this.renderAgentFields(fields)}

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Identifiant technique : {entry.agentId}
        </Typography>

        {cd3 ? (
          this.renderPrompt(cd3)
        ) : entry.isTest ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Texte en service non lu par cet écran (la consigne d'essai n'est pas servie par la lecture
            des agents) : l'éditeur part de la version enregistrée au registre.
          </Typography>
        ) : null}

        <PromptEditor
          entry={entry}
          liveError={registry ? registry.live_error : null}
          onChanged={this.fetchAgents}
          onDirtyChange={this.handleDirtyChange}
        />
      </Paper>
    );
  }

  degradedMessage(agent, isContext) {
    if (agent.status === STATUS_MISSING) {
      return isContext
        ? 'Cet agent n\'existe plus dans le compte Mistral : les analyses tournent toujours, mais sans le résumé de page qu\'il fabrique.'
        : 'Cet agent n\'existe plus dans le compte Mistral : la surface qu\'il couvre n\'est plus analysée.';
    }
    if (agent.status === STATUS_UNREADABLE) {
      return 'Sa configuration n\'a pas pu être lue. La modération elle-même n\'est pas affectée : seul cet écran est aveugle.';
    }
    // Statut hors énum : ne rien SUPPOSER de son sens — le dire, et le nommer.
    return `Cet agent est dans un état que cet écran ne connaît pas (« ${agent.status} »).`;
  }

  renderAgentFields(agent) {
    // Une valeur non lue s'affiche « — », jamais une valeur par défaut plausible : le back
    // ne les invente pas non plus (normalizeAgent nulle tout ce qu'il n'a pas lu).
    const dash = '—';

    const rows = [];

    const group = groupOf(agent);
    const regime = regimeLabel(agent.regime);
    // Un couple (product, object) inconnu ne doit pas s'évaporer non plus : on retombe sur les
    // valeurs brutes plutôt que de perdre l'information.
    const rawScope =
      !group && (agent.product || agent.object)
        ? [agent.product, agent.object].filter(Boolean).join(' · ')
        : null;
    // context_agent porte product/object/regime à `null` PAR CONCEPTION (ce n'est pas une
    // donnée manquante) : on n'affiche donc pas d'usage du tout plutôt qu'un « — » trompeur.
    if (group || regime || rawScope) {
      rows.push([
        'Usage',
        [group ? group.label : null, group ? group.surface : null, rawScope, regime]
          .filter(Boolean)
          .join(' · ')
      ]);
    }

    rows.push(['Modèle', agent.model || dash]);
    rows.push([
      'Température',
      agent.temperature === null || agent.temperature === undefined ? dash : String(agent.temperature)
    ]);

    return (
      <Box>
        {rows.map(([label, value]) => (
          <Stack key={label} direction="row" spacing={1} sx={{ py: 0.25 }}>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>
              {label}
            </Typography>
            <Typography variant="body2">{value}</Typography>
          </Stack>
        ))}
        {this.renderResponseFormat(agent)}
      </Box>
    );
  }

  // D13 — on affiche ce qui S'APPLIQUE, pas ce qui est stocké chez Mistral. La valeur brute
  // vaut `null` sur les 7 agents : l'afficher telle quelle dirait « aucun format imposé »,
  // ce qui est faux (notre code impose le JSON à chaque appel) et pousserait à « réparer »
  // une configuration qui n'a rien de cassé.
  // ⚠ `response_format` reste renseigné même sur une entrée non lue — c'est une constante de
  //   NOTRE code, pas une lecture. Seul `response_format_raw` est null au sens « non lu ».
  renderResponseFormat(agent) {
    if (!agent.response_format) return null;

    return (
      <Stack direction="row" spacing={1} sx={{ py: 0.25 }} alignItems="center">
        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>
          Format de réponse
        </Typography>
        <Typography variant="body2">JSON imposé — par notre appel</Typography>
        <Tooltip
          title={
            'Réglage enregistré côté Mistral : ' +
            formatRawValue(agent.response_format_raw) +
            '. Le format JSON est imposé par la plateforme à chaque analyse, pas par la fiche de l\'agent.'
          }
        >
          <InfoOutlinedIcon fontSize="inherit" color="disabled" />
        </Tooltip>
      </Stack>
    );
  }

  // D7 — le prompt s'affiche EN ENTIER, jamais tronqué : les règles de langue sont ancrées à
  // la fois au début et à la toute dernière ligne (« RAPPEL FINAL … »), et un extrait
  // supprimerait précisément l'ancrage final.
  // ⚠ Replié par défaut : « en entier » n'est pas « déplié d'office » — 7 blocs de 8,6 à
  //   13,6 Ko font ~68 Ko de texte sur la page.
  renderPrompt(agent) {
    if (!agent.instructions) {
      // Un agent lu `ok` SANS prompt n'est pas un cas neutre : isUsableAgentPayload ne valide
      // que `name` + `id` (mistralAgents.js:219), donc un 200 sans `instructions` passe en
      // `ok`. Ne rien rendre ferait de l'agent le plus dangereux — celui dont la consigne a
      // disparu — le plus discret de l'écran.
      if (agent.status !== STATUS_OK) return null;
      return (
        <Alert severity="warning" sx={{ mt: 1 }}>
          Cet agent a été lu, mais Mistral n'a renvoyé aucune consigne. La règle qui gouverne
          cette surface est donc invisible depuis cet écran.
        </Alert>
      );
    }

    return (
      <Accordion
        disableGutters
        elevation={0}
        square
        sx={{ mt: 1, bgcolor: 'transparent' }}
        // Sans ça, replier ne fait que MASQUER : Accordion enveloppe ses enfants dans un
        // Collapse dont `unmountOnExit` vaut false par défaut, donc les ~68 Ko de prompt
        // restent dans le DOM des 7 fiches en permanence.
        TransitionProps={{ unmountOnExit: true }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
          <Typography variant="body2" color="text.secondary">
            {/* [...str] compte les POINTS DE CODE, pas les unités UTF-16. Les prompts GC
                contiennent 9 emoji hors-BMP, que String.length compte double : on afficherait
                8 618 là où le prompt fait 8 609 caractères. */}
            Texte en service ({[...agent.instructions].length.toLocaleString('fr-FR')} caractères)
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0 }}>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 2,
              maxHeight: 480,
              overflow: 'auto',
              bgcolor: 'action.hover',
              borderRadius: 1,
              fontFamily: 'monospace',
              fontSize: '0.75rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word'
            }}
          >
            {agent.instructions}
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  }

  renderVerdictAgents(agents, fromRegistry) {
    // Groupement PAR (product, object), dans l'ordre où l'endpoint les renvoie — DESCRIPTORS
    // fait déjà foi côté back, ce regroupement n'est que de la mise en forme. Un couple
    // inconnu (agent ajouté côté back sans passer ici) atterrit dans un groupe « Autres »
    // plutôt que de disparaître de l'écran.
    // B5_01 — `fromRegistry` : les items sont des entrées jointes CD3 × CD6 (renderRegistryCard,
    // champs product/object/regime remappés) ; la consigne d'essai (product/object null) a sa
    // propre section (renderTestPrompt), jamais le bucket « Autres ».
    const order = [];
    const buckets = {};

    (fromRegistry ? agents.filter((entry) => !entry.isTest) : agents).forEach((agent) => {
      const key = GROUPS[groupKey(agent)] ? groupKey(agent) : 'autres';
      if (!buckets[key]) {
        buckets[key] = [];
        order.push(key);
      }
      buckets[key].push(agent);
    });

    return order.map((key) => {
      const group = GROUPS[key];
      const entries = buckets[key]
        .slice()
        .sort((a, b) => (REGIME_ORDER[a.regime] ?? 9) - (REGIME_ORDER[b.regime] ?? 9));

      return (
        <Box key={key}>
          <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 1 }}>
            <Typography variant="subtitle2">{group ? group.label : 'Autres'}</Typography>
            {group && (
              <Typography variant="caption" color="text.secondary">
                {group.surface}
              </Typography>
            )}
          </Stack>
          <Stack spacing={2}>
            {entries.map((agent) => (fromRegistry ? this.renderRegistryCard(agent) : this.renderAgentCard(agent)))}
          </Stack>
        </Box>
      );
    });
  }

  // B5_01 / D29 — la consigne d'essai (hors production seulement) : section propre, badge,
  // jamais fondue dans les groupes de verdict ni dans « Autres ». Son titre est le nom console
  // (libellé D44 « NE PAS UTILISER — consigne d'essai de l'outil d'administration »).
  renderTestPrompt(entries) {
    const tests = entries.filter((entry) => entry.isTest);
    if (!tests.length) return null;
    return (
      <Box data-testid="prompt-test-section">
        <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 1 }}>
          <Typography variant="subtitle2">Consigne d'essai</Typography>
          <Typography variant="caption" color="text.secondary">
            hors production uniquement — la seule consigne publiable depuis cet environnement, jamais
            utilisée par la modération
          </Typography>
        </Stack>
        <Stack spacing={2}>{tests.map((entry) => this.renderRegistryCard(entry))}</Stack>
      </Box>
    );
  }

  // D20 — amorçage global + rapport nominatif (D36). Le bouton n'apparaît que s'il reste au
  // moins une consigne sans version de référence.
  renderAdoptAll(entries) {
    const { adoptAllBusy, adoptAllReport } = this.state;
    const pending = entries.filter((entry) => entry.reconciliation === 'never_adopted');
    const titleOfId = (agentId) => {
      const entry = entries.find((e) => e.agentId === agentId);
      return entry ? entry.title : agentId;
    };
    return (
      <Box>
        {pending.length > 0 && (
          <Alert severity="info" sx={{ mb: 2 }} data-testid="prompt-adopt-all-banner">
            <AlertTitle>
              {pending.length === 1
                ? 'Une consigne n\'a pas de version de référence'
                : `${pending.length} consignes n'ont pas de version de référence`}
            </AlertTitle>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Tant qu'une consigne n'est pas enregistrée, ses analyses portent « version inconnue »
              sans rattrapage possible, et son édition est désactivée.
            </Typography>
            <Button
              size="small"
              variant="contained"
              disabled={adoptAllBusy}
              onClick={this.adoptAll}
              data-testid="prompt-adopt-all"
            >
              {adoptAllBusy
                ? 'Enregistrement…'
                : `Enregistrer l'état actuel des ${entries.length} consignes comme référence`}
            </Button>
          </Alert>
        )}
        {adoptAllReport && this.renderAdoptAllReport(adoptAllReport, titleOfId)}
      </Box>
    );
  }

  renderAdoptAllReport({ report, error }, titleOfId) {
    if (error) {
      return (
        <Alert severity="error" sx={{ mb: 2 }} onClose={this.dismissReport} data-testid="prompt-adopt-all-report">
          <AlertTitle>Amorçage impossible</AlertTitle>
          <Typography variant="body2">{error.message}</Typography>
        </Alert>
      );
    }
    const ok = ['adopted', 'updated', 'unchanged'].reduce(
      (n, key) => n + (Array.isArray(report[key]) ? report[key].length : 0),
      0
    );
    const skipped = Array.isArray(report.skipped) ? report.skipped : [];
    // Raisons de `skipped[]` (CD7 / 🛑 5b) — `no_instructions` n'est PAS transitoire.
    const reasons = {
      no_instructions: 'lue, mais vide chez Mistral — rien à adopter tant qu\'un texte n\'y est pas posé',
      no_live_state: 'absente de l\'instantané Mistral',
      concurrent_adoption: 'une autre adoption vient d\'aboutir — réessaie',
      write_failed: 'échec d\'écriture au registre — réessaie'
    };
    return (
      <Alert
        severity={skipped.length ? 'warning' : 'success'}
        sx={{ mb: 2 }}
        onClose={this.dismissReport}
        data-testid="prompt-adopt-all-report"
      >
        <AlertTitle>
          {ok} consigne{ok > 1 ? 's' : ''} enregistrée{ok > 1 ? 's' : ''}
          {skipped.length ? ` · ${skipped.length} non enregistrée${skipped.length > 1 ? 's' : ''}` : ''}
        </AlertTitle>
        {skipped.length > 0 && (
          <Box component="ul" sx={{ m: 0, pl: 3 }}>
            {skipped.map((item) => (
              <li key={item.agent_id}>
                <Typography variant="body2">
                  <strong>{titleOfId(item.agent_id)}</strong> —{' '}
                  {reasons[item.reason] ||
                    `${item.reason || 'raison inconnue'}${item.live_status ? ` (${item.live_status})` : ''}`}
                  {' '}— relance possible depuis sa carte (« Adopter l'état actuel »).
                </Typography>
              </li>
            ))}
          </Box>
        )}
      </Alert>
    );
  }

  renderRegistryError(hasContent) {
    const { registryError, error } = this.state;
    if (!registryError) return null;
    const parsed = parseApiError(registryError);
    // Session expirée (CD4+) : CD3 échoue de la même façon et renderError() le dit déjà — une
    // seule alerte, pas deux.
    if (parsed.error === SESSION_EXPIRED && error) return null;
    return (
      <Alert severity="error" sx={{ mb: 2 }} data-testid="prompt-registry-error">
        <AlertTitle>Registre des consignes illisible</AlertTitle>
        <Typography variant="body2">
          L'édition est indisponible tant que le registre ne répond pas ({parsed.message}).
          {hasContent
            ? ' Les consignes ci-dessous sont affichées en lecture, depuis Mistral.'
            : ' Aucune consigne ne peut être affichée.'}
        </Typography>
      </Alert>
    );
  }

  // D8 — l'agent de contexte est montré dans un bloc SÉPARÉ : il fabrique le résumé de page
  // servi à l'agent qui juge, il ne rend lui-même aucun verdict. Le mélanger aux 6 ferait
  // lire « 7 agents de modération », ce qui est faux.
  renderContextAgent(contextAgent) {
    if (!contextAgent) return null;

    return (
      <Box>
        <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 1 }}>
          <Typography variant="subtitle2">Agent de contexte</Typography>
          <Typography variant="caption" color="text.secondary">
            ne rend pas de verdict — prépare le résumé de page servi aux agents ci-dessus
          </Typography>
        </Stack>
        {this.renderAgentCard(contextAgent, true)}
      </Box>
    );
  }

  // D9 — la taxonomie lisible des motifs, dans CET onglet : c'est le vocabulaire que les
  // prompts ci-dessus imposent aux agents, et sans elle l'écran de monitoring se lit
  // « motif 4 : 106 » sans qu'on sache que 4 = racisme.
  // ⛔ Table LUE depuis lib/moderationMotifs.js (contrat CD5, propriétaire B2_01) — jamais
  //    recopiée ici : deux copies divergeraient au premier changement de taxonomie.
  renderMotifs() {
    const ids = Object.keys(MOTIF_LABELS)
      .map(Number)
      .sort((a, b) => a - b);

    return (
      <Box>
        <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 1 }}>
          <Typography variant="subtitle2">Motifs de refus</Typography>
          <Typography variant="caption" color="text.secondary">
            le vocabulaire imposé aux agents par les prompts ci-dessus
          </Typography>
        </Stack>
        {/* Ancre de recette. Les libellés de motifs (« Racisme », « SPAM »…) figurent AUSSI
            dans le texte des prompts : sans un sélecteur propre à ce bloc, une assertion sur
            la taxonomie passe en réalité sur un prompt déplié — faux positif constaté le
            2026-08-27. Attribut invisible, aucun effet sur le rendu. */}
        <Paper variant="outlined" sx={{ p: 2 }} id="moderation-motifs">
          <Accordion
            disableGutters
            elevation={0}
            square
            sx={{ bgcolor: 'transparent' }}
            TransitionProps={{ unmountOnExit: true }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
              <Typography variant="body2" color="text.secondary">
                Taxonomie ({ids.length} motifs) — lecture seule
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0 }}>
              <Box>
                {ids.map((id) => {
                  // Le 25 est produit par les prompts sans exister côté produit (trou T8) :
                  // le montrer comme un motif ordinaire ferait passer une anomalie de prompt
                  // pour une entrée normale de la taxonomie.
                  const outside = isOutOfTaxonomy(id);
                  return (
                    <Stack
                      key={id}
                      direction="row"
                      spacing={1}
                      alignItems="baseline"
                      sx={{ py: 0.25 }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ minWidth: 40, textAlign: 'right' }}
                      >
                        {id}
                      </Typography>
                      <Typography variant="body2">{MOTIF_LABELS[id]}</Typography>
                      {outside && (
                        <Chip size="small" color="warning" label="hors taxonomie produit" />
                      )}
                    </Stack>
                  );
                })}
              </Box>
            </AccordionDetails>
          </Accordion>
        </Paper>
      </Box>
    );
  }

  render() {
    const { data, isLoading, error, diffOpen, registry } = this.state;
    const agents = data && Array.isArray(data.agents) ? data.agents : [];
    const fetchedAt = data ? formatFetchedAt(data.fetched_at) : null;
    // B5_01 — un RAFRAÎCHISSEMENT ne démonte plus le contenu : chaque écriture (brouillon,
    // publication, adoption…) relance fetchAgents(), et démonter les cartes pendant l'appel
    // détruirait l'état des éditeurs (texte, message de résultat). Seul le chargement INITIAL
    // (aucune donnée encore) affiche le spinner seul ; ensuite l'ancien contenu reste rendu et
    // le bouton « Rafraîchir » est désactivé le temps de l'appel.
    const initialLoading = isLoading && !data && !registry;
    // B5_02 — UNE dérivation partagée par le bouton ET le montage du dialogue : le bouton n'est
    // actif que si <PromptDiff> est réellement rendu (même condition que le bloc CD3 ci-dessous).
    // Sinon : `error` posé sans effacer `data` (503 Mistral au rafraîchissement) ou session
    // expirée (désormais une erreur levée par le service, CD4+) donnaient un clic mort.
    const hasContent = !initialLoading && !error && Boolean(data) && (agents.length > 0 || Boolean(data.context_agent));
    // B5_01 — le registre (CD6) est rendu dès qu'il est lu, même quand CD3 est en 503 (L3) :
    // cartes dégradées (pas de texte en service), bandeau « non vérifiable », édition et
    // brouillon possibles, publication bloquée. `entries` = null tant que le registre n'a pas
    // répondu → repli sur le rendu CD3 seul, en lecture (comportement B2_03).
    const entries = !initialLoading && registry ? joinRegistryWithLive(registry, hasContent ? data : null) : null;
    const showRegistry = Boolean(entries && entries.length);
    const showLegacy = !showRegistry && hasContent;
    const registryFetchedAt = registry ? formatFetchedAt(registry.fetched_at) : null;

    return (
      <Box>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          {/* B5_01 — remplace « lecture seule — édition à venir (Phase 2) » : ce qui s'édite, ce
              qui ne s'édite pas, et pourquoi. */}
          <Typography variant="body2" color="text.secondary">
            Consignes des agents Mistral : chaque agent de verdict s'édite depuis sa carte (brouillon,
            publication, historique) ; l'agent de contexte reste en lecture seule, et un agent introuvable
            chez Mistral ne s'édite pas.
          </Typography>
          <Stack direction="row" spacing={1}>
            {/* B5_02 — actif dès que le bloc de données CD3 est rendu (`hasContent`, même condition
                que le montage du dialogue ci-dessous). La règle d'éligibilité (absence de consigne,
                jamais le statut) vit dans l'inventaire de PromptDiff, pas ici : avec 0 ou 1 consigne
                lisible, le dialogue s'ouvre et DIT pourquoi rien n'est comparable (entrées grisées
                avec leur raison) — un bouton gris sans explication serait le « raccourci en
                silence » que CD3+ existe pour fermer. */}
            <Button
              variant="outlined"
              size="small"
              disabled={!hasContent}
              onClick={this.openDiff}
              data-testid="prompt-diff-open"
            >
              Comparer les consignes
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              disabled={isLoading}
              onClick={this.onRefresh}
            >
              Rafraîchir
            </Button>
          </Stack>
        </Stack>

        {initialLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {!initialLoading && error && this.renderError()}
        {!initialLoading && this.renderRegistryError(hasContent)}

        {/* Un 200 hors-contrat (forme changée en amont, API bêta) rendrait sinon un écran vide
            et muet, avec un <Divider/> orphelin — l'admin conclurait « aucun agent configuré »,
            ce qui est très différent de « je n'ai pas su lire la réponse ». */}
        {!initialLoading && !error && data && !agents.length && !data.context_agent && !showRegistry && (
          <Alert severity="warning">
            La réponse du serveur ne contient aucun agent. Ce n'est pas une configuration vide :
            c'est une réponse que cet écran ne sait pas lire.
          </Alert>
        )}

        {(showRegistry || showLegacy) && (
          <Box>
            {this.renderMissingBanner(agents)}
            {showRegistry && this.renderAdoptAll(entries)}
            <Stack spacing={3}>
              {showRegistry ? this.renderVerdictAgents(entries, true) : this.renderVerdictAgents(agents, false)}
              {showRegistry && this.renderTestPrompt(entries)}
              {hasContent && data.context_agent && <Divider />}
              {hasContent && this.renderContextAgent(data.context_agent)}
              <Divider />
              {this.renderMotifs()}
            </Stack>
            {/* Pas décoratif : les prompts sont éditables à la console Mistral sans aucune
                trace de notre côté (DA10). Deux horodatages, deux lectures distinctes : CD3 (ce
                qui tourne) et CD6 (le registre comparé à l'état réel) — B5_01. */}
            {fetchedAt && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 3 }}
              >
                Configuration lue chez Mistral le {fetchedAt}.
              </Typography>
            )}
            {registryFetchedAt && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: fetchedAt ? 0 : 3 }}
                data-testid="prompt-registry-fetched-at"
              >
                Registre comparé à l'état réel le {registryFetchedAt}.
              </Typography>
            )}
            {/* B5_02 — dialogue de comparaison (plein écran, porté par PromptDiff). Monté avec la
                donnée CD3 déjà en mémoire : aucun appel réseau supplémentaire. */}
            {hasContent && (
              <PromptDiff open={diffOpen} data={data} fetchedAt={fetchedAt} onClose={this.closeDiff} />
            )}
          </Box>
        )}
      </Box>
    );
  }
}

export default Agents;
